import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { useRoutesState } from '@clatter/platform';
import { DataGridPremium as MaterialDataGrid, useGridApiRef } from '@mui/x-data-grid-premium';

import { StyledDataGridPremium } from './index';
import { DataGridCustomToolbar } from './components/DataGridCustomToolbar';

// helper to deep merge user initialState with persisted state
// persisted state takes priority to preserve user's column order and other settings
const mergeInitialState = (persistedState, userInitialState) => {
  if (!persistedState && !userInitialState) return null;
  if (!persistedState) return userInitialState;
  if (!userInitialState) return persistedState;

  return {
    ...userInitialState,
    ...persistedState,
    columns: {
      ...userInitialState.columns,
      ...persistedState.columns,
      columnVisibilityModel: persistedState.columns?.columnVisibilityModel || userInitialState.columns?.columnVisibilityModel,
    },
    rowSelection: persistedState.rowSelection || userInitialState.rowSelection || {},
  };
};

// this method will check if filters in the persisted state are
// still available in the columns definition, and if not it will remove those
// from state so Data Grid wouldn't break on render
const compareFiltersState = (persistedState, columns) => {
  if (!persistedState) return persistedState;

  const clonedState = cloneDeep(persistedState);

  if (clonedState?.filter?.filterModel?.items) {
    const updatedFilterModel = clonedState.filter.filterModel.items.map((item) => {
      if (!item) return null;

      // migrate v5 format to v6 format
      const field = item.field || item.columnField;
      const operator = item.operator || item.operatorValue;

      // if this is v5 format, create a new v6 format item
      const migratedItem = {
        ...item,
        field,
        operator,
      };

      // remove old v5 properties if they exist
      delete migratedItem.columnField;
      delete migratedItem.operatorValue;

      const columnData = columns.find((column) => column.field === field);

      // if columnData is not found, or it doesn't have filterOperators, keep the filter.
      if (!columnData || !columnData.filterOperators) {
        return migratedItem;
      }

      const hasPersistedFilterDefinition = columnData.filterOperators.some((op) => op.value === operator);

      return hasPersistedFilterDefinition ? migratedItem : null;
    });

    clonedState.filter.filterModel.items = updatedFilterModel.filter((item) => item !== null);

    // migrate linkOperator to logicOperator if present
    if (clonedState.filter.filterModel.linkOperator) {
      clonedState.filter.filterModel.logicOperator = clonedState.filter.filterModel.linkOperator;
      delete clonedState.filter.filterModel.linkOperator;
    }
  }

  // migrate v7 to v8 rowSelection state
  if (!clonedState.rowSelection) {
    clonedState.rowSelection = {};
  }

  return clonedState;
};

const DataGridPremium = ({
  apiRef: customApiRef,
  columns,
  getRowId,
  loading = false,
  name: tableName = 'table', // used to store in the persistence layer, should be unique per route
  onRowClick: customOnRowClick,
  rows,
  selectable,
  dataGridProps,
  renderCustomToolbar,
  onRowsPerSizeChange,
  initialRowsPerPage = 25,
  showVerticalSidebar = false,
  tableHeight = 'auto',
  defaultSortField,
  disableColumnFilter,
  rowsPerPageOptions,
  selectedRowsPerPage,
  multilineItemsLimit,
  disablePersist,
  rowAutoHeight = false,
  wrapContent = false,
  enableRowDeselect = false,
}) => {
  const apiRef = customApiRef || useGridApiRef();
  // const apiRef = useGridApiRef();
  const { setRouteStateProperty, getRouteStateProperty } = useRoutesState();
  const persistedState = getRouteStateProperty(tableName);

  // track if we're currently saving state to prevent infinite loops
  const isSavingStateRef = React.useRef(false);

  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  const { components = {}, slotProps: userSlotProps, initialState: userInitialState, ...restDataGridProps } = dataGridProps || {};

  //region METHODS
  const handleRowsPerPageChange = (nextRowsPerPage) => {
    if (onRowsPerSizeChange) {
      onRowsPerSizeChange(nextRowsPerPage);
    }
    setRowsPerPage(nextRowsPerPage);
  };

  const handlePaginationModelChange = (newModel) => {
    handleRowsPerPageChange(newModel.pageSize);
  };

  const handleStateChange = useCallback(
    async (params) => {
      if (disablePersist) {
        return;
      }

      // prevent infinite loops by skipping if we're already saving
      if (isSavingStateRef.current) {
        return;
      }

      if (!apiRef?.current?.exportState) {
        console.debug('No Data Grid api ref!');
        return;
      }

      const state = apiRef?.current?.exportState();

      const withColumnVisibility = {
        ...state,
        pinnedColumns: params?.pinnedColumns,
        columns: {
          ...state?.columns,
          columnVisibilityModel: params?.columns?.columnVisibilityModel || {},
        },
      };

      // if there is no change in the grid state
      // do not trigger redux update, so we can avoid
      // unnecessary updates and app slow down
      if (isEqual(withColumnVisibility, persistedState)) {
        return;
      }

      // save state to persist reducer
      isSavingStateRef.current = true;
      try {
        await setRouteStateProperty({
          [tableName]: withColumnVisibility,
        });
      } finally {
        // reset the flag after a short delay to allow the state to settle
        // setTimeout(() => {
        //   isSavingStateRef.current = false;
        // }, 100);
        isSavingStateRef.current = false;
      }
    },
    [disablePersist, tableName, setRouteStateProperty, persistedState, apiRef],
  );

  const handleRowClick = useCallback(
    (params, event) => {
      if (enableRowDeselect) {
        const id = params.id;
        const isSelected = apiRef.current.isRowSelected(id);

        // only intercept a plain click (no Shift/Ctrl/Cmd).
        const plainClick = !event.shiftKey && !event.ctrlKey && !event.metaKey;

        if (isSelected && plainClick) {
          // stop the grid's default selection on click
          event.defaultMuiPrevented = true;
          // deselect the row
          apiRef.current.selectRow(id, false);
          return;
        }
      }

      if (typeof customOnRowClick === 'function') {
        customOnRowClick(params, event);
      }
    },
    [apiRef, customOnRowClick],
  );

  const handleGetRowHeight = (params) => {
    if (rowAutoHeight) {
      return 'auto';
    }

    // skip (return default), if multiline items limit is not set
    if (multilineItemsLimit === undefined) {
      return null;
    }

    // let's start by getting visible columns with "multiline" prop set to true
    // then, check if current row has data that meets the "multiline" policy
    const visibleMultilineColumns = (apiRef?.current?.getVisibleColumns() || []).filter((column) => !!column.multiline);

    // skip (return default), if there are NO visible multiline columns
    if (!visibleMultilineColumns.length) {
      return null;
    }

    let isRowAutoHeight = false;

    visibleMultilineColumns.forEach((column) => {
      if (isRowAutoHeight) {
        return;
      }

      // note: this assumes that the "multiline" columns has array data
      if ((params?.model?.[column.field] || []).length > 2) {
        isRowAutoHeight = true;
      }
    });

    return isRowAutoHeight ? 'auto' : null;
  };
  //endregion

  //region EFFECTS
  useEffect(() => {
    let mounted = true;
    if (mounted) {
      if (selectedRowsPerPage && typeof selectedRowsPerPage === 'number') {
        setRowsPerPage(selectedRowsPerPage);
      }
    }

    return () => (mounted = false);
  }, [selectedRowsPerPage]);
  //endregion

  return (
    <StyledDataGridPremium
      $showVerticalSidebar={showVerticalSidebar}
      $wrapContent={wrapContent}
      style={{ height: tableHeight === 'auto' ? 'unset' : tableHeight }}
    >
      <div className="wrapper" style={{ height: tableHeight === 'auto' ? 'unset' : '100%' }}>
        <MaterialDataGrid
          apiRef={apiRef}
          // state
          rows={rows}
          columns={columns}
          initialState={
            persistedState
              ? compareFiltersState(mergeInitialState(persistedState, userInitialState), columns)
              : userInitialState || {
                  pagination: { paginationModel: { page: 0, pageSize: initialRowsPerPage } },
                  sorting: { sortModel: defaultSortField ? [defaultSortField] : [] },
                }
          }
          // handlers
          onStateChange={handleStateChange} // handle state persist
          onPaginationModelChange={handlePaginationModelChange}
          onRowClick={(params, event) => handleRowClick(params, event)}
          // customizations
          slots={{
            toolbar: DataGridCustomToolbar,
          }}
          slotProps={{
            ...userSlotProps,
            toolbar: {
              renderCustomToolbar,
            },
          }}
          autoPageSize={false}
          autoHeight={tableHeight === 'auto'}
          pageSizeOptions={rowsPerPageOptions || [25, 50, 100]}
          getRowId={getRowId}
          loading={loading}
          disableColumnFilter={disableColumnFilter}
          getRowHeight={handleGetRowHeight}
          checkboxSelection={selectable}
          pagination
          showToolbar
          {...restDataGridProps}
        />
      </div>
    </StyledDataGridPremium>
  );
};

DataGridPremium.propTypes = {
  name: PropTypes.string.isRequired,
};

export default DataGridPremium;
